import { useEffect, useState } from 'react';
import { get, post } from './api';
import { match, navigate, usePath } from './router';
import { useLibrary, type User } from './library';
import { Spinner } from './ui/controls';
import { Toasts } from './ui/toast';
import { Login, Setup } from './pages/Auth';
import { Dashboard } from './pages/Dashboard';
import { Customers } from './pages/Customers';
import { EventLibrary, Fonts, Users } from './pages/Library';
import { EditorPage } from './editor/Editor';
import { ProofPage } from './pages/Proof';

export function App() {
  const path = usePath();
  const proof = match('/proof/:token', path);
  if (proof) return <ProofPage token={proof.token} />;
  return <StaffApp path={path} />;
}

function StaffApp({ path }: { path: string }) {
  const [state, setState] = useState<'loading' | 'setup' | 'login' | 'ready'>('loading');
  const user = useLibrary((s) => s.user);
  const signedIn = (u: User) => { useLibrary.setState({ user: u }); setState('ready'); void useLibrary.getState().loadFonts(); };

  useEffect(() => {
    (async () => {
      try {
        const { needsSetup } = await get<{ needsSetup: boolean }>('/api/setup');
        if (needsSetup) return setState('setup');
        const { user } = await get<{ user: User }>('/api/auth/me');
        signedIn(user);
      } catch {
        setState('login');
      }
    })();
    const onUnauth = () => setState('login');
    window.addEventListener('cs:unauthorized', onUnauth);
    return () => window.removeEventListener('cs:unauthorized', onUnauth);
  }, []);

  if (state === 'loading') return <div className="page"><Spinner label="Loading…" /></div>;
  if (state === 'setup') return <><Setup onDone={signedIn} /><Toasts /></>;
  if (state === 'login' || !user) return <><Login onDone={signedIn} /><Toasts /></>;

  const editor = match('/projects/:id', path);
  if (editor) return <div className="app"><EditorPage key={editor.id} id={editor.id} /><Toasts /></div>;
  const nav = [['/', 'Projects'], ['/customers', 'Customers'], ['/events', 'Holidays & events'], ['/fonts', 'Fonts'], ...(user.role === 'admin' ? [['/users', 'Users']] : [])];
  const base = '/' + path.split('?')[0].split('/')[1];
  return (
    <div className="app">
      <header className="topbar">
        <div className="brand"><img src="/favicon.svg" width={24} height={24} alt="" />Calendar Studio</div>
        <nav className="row" aria-label="Main">
          {nav.map(([href, label]) => <button key={href} className={`navlink ${base === href ? 'on' : ''}`} aria-current={base === href ? 'page' : undefined} onClick={() => navigate(href)}>{label}</button>)}
        </nav>
        <span className="spacer" />
        <span className="small" style={{ color: '#c8d0da' }}>{user.name}</span>
        <button className="navlink" onClick={async () => { await post('/api/auth/logout'); useLibrary.setState({ user: null }); setState('login'); }}>Sign out</button>
      </header>
      {base === '/customers' ? <Customers /> : base === '/events' ? <EventLibrary /> : base === '/fonts' ? <Fonts /> : base === '/users' ? <Users me={user} /> : <Dashboard />}
      <Toasts />
    </div>
  );
}
